import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  Chip,
  MenuItem,
  Tabs,
  Tab,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Menu,
  Badge,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import InputIcon from '@mui/icons-material/Input';
import OutputIcon from '@mui/icons-material/Output';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import EntityImportExportButtons from '../components/EntityImportExportButtons';
import {
  stockItemService,
  stockTransactionService,
  stockArrivalService,
  rawMaterialService,
  dyeService,
  packagingMaterialService,
  productionOrderService,
  consumableMaterialService,
  warehouseService,
} from '../services/api';
import api from '../services/api';
import { navigateWithReturn, stockReturnLabel, stockReturnTo } from '../utils/pageBack';
import CloseIcon from '@mui/icons-material/Close';


/** Кількість без «хвостів» з плаваючою комою: 417.86400000000003 → 417,864 */
const fmtQty = (v) => Number(v).toLocaleString('uk-UA', { maximumFractionDigits: 3 });

const STOCK_KIND_TITLES = {
  raw: 'Склад сировини',
  dyes: 'Склад барвників',
  packaging: 'Склад упаковки',
  technical: 'Склад техматеріалів',
  semi: 'Склад напівфабрикатів',
  packaging_work: 'Склад для пакування',
  consumable: 'Склад розхідних матеріалів',
};

/** Fallback, якщо API /warehouses/ недоступний (той самий реєстр, що на бекенді). */
const FALLBACK_TRANSFER_WAREHOUSES = [
  { id: 'production', name: 'Склад виробництва' },
  { id: 'raw', name: 'Склад сировини' },
  { id: 'dyes', name: 'Склад барвників' },
  { id: 'packaging', name: 'Склад упаковки' },
  { id: 'technical', name: 'Склад техматеріалів' },
  { id: 'semi', name: 'Склад напівфабрикатів' },
  { id: 'packaging_work', name: 'Склад для пакування' },
  { id: 'finished', name: 'Склад готової продукції' },
  { id: 'crushing_waste', name: 'Склад перемолів' },
  { id: 'blocking', name: 'Склад блокування' },
  { id: 'consumable', name: 'Склад розхідних матеріалів' },
  { id: 'defect', name: 'Склад браку' },
  { id: 'samples', name: 'Склад взірців' },
];

/** stockKind вкладки → id складу в /warehouses/ */
function stockKindToWarehouseId(kind) {
  if (!kind) return null;
  if (kind === 'packaging-work' || kind === 'packaging_work') return 'packaging_work';
  if (kind === 'crushing-waste' || kind === 'crushing_waste') return 'crushing_waste';
  return kind;
}

/** Довгий список у Select не виходить за екран (прокрутка всередині меню). */
const SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: 'min(40vh, 320px)',
    },
  },
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
};

function defaultTransferDest(options, sourceId) {
  if (sourceId !== 'production') {
    const prod = options.find((w) => w.id === 'production');
    if (prod) return 'production';
  }
  return options[0]?.id || '';
}

const STOCK_KIND_CATEGORY = {
  raw: 'raw_material',
  dyes: 'dye',
  packaging: 'packaging',
  technical: 'raw_material',
  semi: null,
  packaging_work: null,
  consumable: 'consumable',
};

/** Одиниця за типом матеріалу (fallback, якщо немає unit у залишку / довіднику). */
function defaultUnitForCategory(category) {
  switch (category) {
    case 'packaging':
    case 'packaging_material':
    case 'consumable':
    case 'consumable_material':
      return 'шт';
    case 'raw_material':
    case 'dye':
    default:
      return 'кг';
  }
}

function unitForCategory(category, stockItem, catalogItem) {
  const u = (stockItem?.unit && String(stockItem.unit).trim()) || '';
  if (u) return u;
  const cu = (catalogItem?.unit && String(catalogItem.unit).trim()) || '';
  if (cu) return cu;
  return defaultUnitForCategory(category);
}

/** raw / technical — розділення складів сировини та техматеріалів (поле store_category у довіднику). */
function rawStoreSlotFromItem(item) {
  return item?.raw_material_store_category === 'technical' ? 'technical' : 'raw';
}

function rawStoreSlotFromNested(d) {
  return d?.raw_material_store_category === 'technical' ? 'technical' : 'raw';
}

/** Перемол ведеться лише на Складі перемолів (CrushingWasteLot), не на Складі сировини. */
function isRemeltMaterialItem(item) {
  const t = String(item?.raw_material_type || item?.type || '').trim().toLowerCase();
  if (t === 'перемол') return true;
  const article = String(
    item?.raw_material_article || item?.article || '',
  ).trim().toUpperCase();
  return article.startsWith('PEREMOL');
}

function isRemeltMaterialNested(d) {
  const t = String(d?.raw_material_type || d?.type || '').trim().toLowerCase();
  if (t === 'перемол') return true;
  const article = String(d?.raw_material_article || d?.article || '').trim().toUpperCase();
  return article.startsWith('PEREMOL');
}

function isCrushingRemeltArrival(a) {
  return a?.crushing_operation != null && a?.crushing_operation !== '';
}

function StockPage({ stockKind }) {
  const navigate = useNavigate();
  const [stockItems, setStockItems] = useState([]);
  const [packagingRequests, setPackagingRequests] = useState([]);
  const [refsAnchorEl, setRefsAnchorEl] = useState(null);
  const refsMenuOpen = Boolean(refsAnchorEl);
  const [transactions, setTransactions] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [dyes, setDyes] = useState([]);
  const [packagingMaterials, setPackagingMaterials] = useState([]);
  const [consumableMaterials, setConsumableMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const isProductWarehouse = stockKind === 'semi' || stockKind === 'packaging_work';
  const [tabValue, setTabValue] = useState(isProductWarehouse ? 2 : 0);
  const [arrivals, setArrivals] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [qtyFilter, setQtyFilter] = useState('all'); // all | gt0 | eq0 | lt0
  const [txTypeFilter, setTxTypeFilter] = useState('all'); // all | in | out
  const [txDateFrom, setTxDateFrom] = useState('');
  const [txDateTo, setTxDateTo] = useState('');
  const [sortKey, setSortKey] = useState('name'); // name | article | quantity | updated
  const [sortDir, setSortDir] = useState('asc'); // asc | desc
  const [arrivalDialogOpen, setArrivalDialogOpen] = useState(false);
  const [selectedArrival, setSelectedArrival] = useState(null);
  const [arrivalActualQty, setArrivalActualQty] = useState('');
  const [transferHubOpen, setTransferHubOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferItem, setTransferItem] = useState('');
  const [transferQty, setTransferQty] = useState('');
  const [transferDest, setTransferDest] = useState('production');
  const [transferWarehouses, setTransferWarehouses] = useState(FALLBACK_TRANSFER_WAREHOUSES);
  const [transferNote, setTransferNote] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferError, setTransferError] = useState('');
  const [shortageOpen, setShortageOpen] = useState(false);
  const [shortages, setShortages] = useState([]);
  const [shortageLoading, setShortageLoading] = useState(false);
  const [shortageBadge, setShortageBadge] = useState(0);

  // Заявки
  const [showRequests, setShowRequests] = useState(false);
  const [requestsTab, setRequestsTab] = useState(0); // 0=Нові, 1=Історія
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsBadge, setRequestsBadge] = useState(0);
  const [newReqDialog, setNewReqDialog] = useState(false);
  const [reqForm, setReqForm] = useState({ requester_name: '', stock_item_id: '', quantity: '' });
  const [transferReqLoading, setTransferReqLoading] = useState(false);
  const [transferReqDialog, setTransferReqDialog] = useState(null); // full req object
  const [transferReqQty, setTransferReqQty] = useState('');
  const getCurrentUserName = () => { try { const u = JSON.parse(localStorage.getItem('currentUser') || 'null'); return u?.full_name || u?.username || ''; } catch { return ''; } };
  const [formData, setFormData] = useState({
    item_category: 'raw_material',
    raw_material: '',
    dye: '',
    packaging_material: '',
    consumable_material: '',
    transaction_type: 'in',
    quantity: '',
    unit_price: '',
    comment: '',
  });
  /** Підказка рекомендованої ціни: { text } або null */
  const [priceHint, setPriceHint] = useState(null);
  const priceHintReqRef = React.useRef(0);

  const requestsQueryParams = useMemo(() => {
    const params = {};
    if (stockKind) params.stock_kind = stockKind;
    return params;
  }, [stockKind]);

  const refreshRequestsBadge = useCallback(() => {
    api.get('/stock-material-requests/', { params: { status: 'new', ...requestsQueryParams } })
      .then((r) => {
        const d = r.data?.results ?? r.data ?? [];
        setRequestsBadge(Array.isArray(d) ? d.length : 0);
      })
      .catch(() => setRequestsBadge(0));
  }, [requestsQueryParams]);

  const sourceWarehouseId = stockKindToWarehouseId(stockKind);

  /** Усі активні склади, крім того, з якого переміщуємо. */
  const transferDestOptions = useMemo(
    () => transferWarehouses.filter((w) => w.id !== sourceWarehouseId),
    [transferWarehouses, sourceWarehouseId],
  );

  useEffect(() => {
    loadData();
    api.get('/material-shortages/').then(r => setShortageBadge((r.data || []).length)).catch(() => {});
    refreshRequestsBadge();
    warehouseService.getAll(sourceWarehouseId ? { exclude: sourceWarehouseId } : {})
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        if (list.length) setTransferWarehouses(list);
      })
      .catch(() => {});
  }, [stockKind]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setTransferDest(defaultTransferDest(transferDestOptions, sourceWarehouseId));
  }, [transferDestOptions, sourceWarehouseId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [stockItemsRes, transactionsRes, rawRes, dyesRes, packRes, arrivalsRes, pkgReqRes, consumableRes] =
        await Promise.all([
          stockItemService.getAll(),
          stockTransactionService.getAll(),
          rawMaterialService.getAll(),
          dyeService.getAll(),
          packagingMaterialService.getAll(),
          stockArrivalService.getAll({ status: 'pending', exclude_crushing: true }),
          stockKind === 'packaging_work'
            ? productionOrderService.packagingRequests()
            : Promise.resolve({ data: [] }),
          consumableMaterialService.getAll(),
        ]);
      let stockItemsData = [];
      let transactionsData = [];
      let rawMaterialsData = [];
      let dyesData = [];
      let packagingData = [];
      if (stockItemsRes.data) {
        stockItemsData = Array.isArray(stockItemsRes.data.results) ? stockItemsRes.data.results : Array.isArray(stockItemsRes.data) ? stockItemsRes.data : [];
      }
      if (transactionsRes.data) {
        transactionsData = Array.isArray(transactionsRes.data.results) ? transactionsRes.data.results : Array.isArray(transactionsRes.data) ? transactionsRes.data : [];
      }
      if (rawRes.data) {
        rawMaterialsData = Array.isArray(rawRes.data.results) ? rawRes.data.results : Array.isArray(rawRes.data) ? rawRes.data : [];
      }
      if (dyesRes.data) {
        dyesData = Array.isArray(dyesRes.data.results) ? dyesRes.data.results : Array.isArray(dyesRes.data) ? dyesRes.data : [];
      }
      if (packRes.data) {
        packagingData = Array.isArray(packRes.data.results) ? packRes.data.results : Array.isArray(packRes.data) ? packRes.data : [];
      }
      let arrivalsData = [];
      if (arrivalsRes.data) {
        arrivalsData = Array.isArray(arrivalsRes.data.results)
          ? arrivalsRes.data.results
          : Array.isArray(arrivalsRes.data)
          ? arrivalsRes.data
          : [];
      }
      setStockItems(stockItemsData);
      setTransactions(transactionsData);
      setRawMaterials(rawMaterialsData);
      setDyes(dyesData);
      setPackagingMaterials(packagingData);
      const consumableData = Array.isArray(consumableRes.data?.results) ? consumableRes.data.results : Array.isArray(consumableRes.data) ? consumableRes.data : [];
      setConsumableMaterials(consumableData);
      setArrivals(arrivalsData);
      const pr = pkgReqRes?.data;
      setPackagingRequests(Array.isArray(pr) ? pr : pr?.results || []);
    } catch (error) {
      console.error('Помилка завантаження:', error);
      alert(`Помилка завантаження даних: ${error.message || 'Невідома помилка'}`);
    } finally {
      setLoading(false);
    }
  };

  const defaultItemCategory = stockKind ? (STOCK_KIND_CATEGORY[stockKind] || 'raw_material') : 'raw_material';
  const lockCategory = Boolean(stockKind && stockKind !== 'semi');
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const searchTokens = normalizedSearchQuery ? normalizedSearchQuery.split(/\s+/u).filter(Boolean) : [];

  const formatArticleName = (article, name) => {
    const a = typeof article === 'string' ? article.trim() : '';
    const n = typeof name === 'string' ? name.trim() : '';
    if (a && n) return `${a} — ${n}`;
    if (a) return a;
    return n || '-';
  };

  const handleOpen = (type = 'in') => {
    priceHintReqRef.current += 1;
    setPriceHint(null);
    setFormData({
      item_category: lockCategory ? defaultItemCategory : defaultItemCategory,
      raw_material: '',
      dye: '',
      packaging_material: '',
      consumable_material: '',
      transaction_type: type,
      quantity: '',
      unit_price: '',
      comment: '',
    });
    setOpen(true);
  };

  const [txnSaving, setTxnSaving] = useState(false);

  const handleClose = () => {
    if (txnSaving) return;
    setOpen(false);
    setPriceHint(null);
  };

  const formatRecommendedPriceHint = (data, formUnit) => {
    if (!data || data.unit_price == null) return null;
    const price = Number(data.unit_price);
    if (!Number.isFinite(price)) return null;
    const priceStr = price.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const unit = formUnit || data.unit || 'од';
    let text = `Рекомендовано: ${priceStr} грн/${unit} — ${data.source_label || 'система'}`;
    if (data.date) text += ` ${data.date}`;
    return text;
  };

  /** Підставити рекомендовану ціну після вибору матеріалу (лише для приходу). */
  const applyRecommendedPrice = async (category, materialId, nextFormPatch = {}) => {
    const reqId = ++priceHintReqRef.current;
    if (formData.transaction_type !== 'in') {
      setFormData((prev) => ({ ...prev, ...nextFormPatch }));
      return;
    }
    if (!materialId) {
      setPriceHint(null);
      setFormData((prev) => ({ ...prev, ...nextFormPatch, unit_price: '' }));
      return;
    }
    setFormData((prev) => ({ ...prev, ...nextFormPatch, unit_price: '' }));
    setPriceHint(null);
    try {
      const res = await stockTransactionService.recommendedPrice({
        category,
        material_id: materialId,
      });
      if (reqId !== priceHintReqRef.current) return;
      const data = res?.data;
      if (data?.unit_price == null) {
        setPriceHint(null);
        return;
      }
      let catalogItem = null;
      if (category === 'packaging') {
        catalogItem = packagingMaterials.find((p) => String(p.id) === String(materialId)) || null;
      }
      const unit = unitForCategory(category, null, catalogItem);
      setFormData((prev) => ({
        ...prev,
        ...nextFormPatch,
        unit_price: String(data.unit_price),
      }));
      setPriceHint({ text: formatRecommendedPriceHint(data, unit) });
    } catch (err) {
      if (reqId !== priceHintReqRef.current) return;
      setPriceHint(null);
    }
  };

  const handleSubmit = async () => {
    if (txnSaving) return;
    try {
      const qty = parseFloat(formData.quantity) || 0;
      const payload = {
        transaction_type: formData.transaction_type,
        quantity: qty,
        comment: formData.comment,
      };
      if (formData.item_category === 'raw_material') payload.raw_material = formData.raw_material || null;
      else if (formData.item_category === 'dye') payload.dye = formData.dye || null;
      else if (formData.item_category === 'packaging') payload.packaging_material = formData.packaging_material || null;
      else if (formData.item_category === 'consumable') payload.consumable_material = formData.consumable_material || null;
      if (!payload.raw_material && !payload.dye && !payload.packaging_material && !payload.consumable_material) {
        alert('Оберіть сировину, барвник, упаковку або розхідний матеріал');
        return;
      }
      if (formData.transaction_type === 'out') {
        if (qty <= 0) {
          alert('Вкажіть кількість більше 0');
          return;
        }
        const si = outSelectedStockItem;
        const avail = stockAvailableQty(si);
        if (!si || avail <= 0) {
          alert('Оберіть позицію з доступним залишком на цьому складі');
          return;
        }
        if (qty > avail + 1e-9) {
          alert(`Недостатньо на складі: доступно ${avail} ${si.unit || ''}`.trim());
          return;
        }
      }
      if (formData.transaction_type === 'in' && formData.unit_price !== '') {
        const p = parseFloat(formData.unit_price);
        if (Number.isFinite(p) && p >= 0) payload.unit_price = p;
      }
      setTxnSaving(true);
      await stockTransactionService.create(payload);
      setOpen(false);
      loadData();
    } catch (error) {
      console.error('Помилка збереження:', error);
      const errorMessage = error.response?.data ? JSON.stringify(error.response.data, null, 2) : error.message || 'Помилка збереження даних';
      alert(`Помилка збереження даних:\n${errorMessage}`);
    } finally {
      setTxnSaving(false);
    }
  };

  const getItemDisplayName = (item) => {
    if (item.item_category === 'raw_material') {
      return item.raw_material_name || '-';
    }
    if (item.item_category === 'dye') {
      return item.dye_name || '-';
    }
    if (item.item_category === 'packaging') {
      return item.packaging_material_name || '-';
    }
    if (item.item_category === 'product') {
      return item.product_name || '-';
    }
    if (item.item_category === 'consumable') {
      return item.consumable_material_name || '-';
    }
    return item.raw_material_name || item.dye_name || item.packaging_material_name || item.product_name || item.consumable_material_name || '-';
  };

  const getItemArticle = (item) => {
    if (item.item_category === 'raw_material') return item.raw_material_article || '-';
    if (item.item_category === 'dye') return item.dye_article || '-';
    if (item.item_category === 'packaging') return item.packaging_material_article || '-';
    if (item.item_category === 'product') return item.product_article || '-';
    if (item.item_category === 'consumable') return item.consumable_material_article || '-';
    return item.raw_material_article || item.dye_article || item.packaging_material_article || item.product_article || item.consumable_material_article || '-';
  };

  const handleExportTransactions = () => {
    const rows = filteredTransactions.map((t) => {
      const si = t.stock_item_data || {};
      const name =
        si.raw_material_name || si.dye_name || si.packaging_material_name || si.product_name || '—';
      const article =
        si.raw_material_article || si.dye_article || si.packaging_material_article || si.product_article || '—';
      const cat =
        si.item_category === 'raw_material' ? 'Сировина'
        : si.item_category === 'dye' ? 'Барвник'
        : si.item_category === 'packaging' ? 'Упаковка'
        : si.item_category === 'product' ? 'Готова продукція'
        : '—';
      return {
        'Дата': t.created_at ? new Date(t.created_at).toLocaleString('uk-UA') : '—',
        'Категорія': cat,
        'Артикул': article,
        'Назва': name,
        'Тип операції': t.transaction_type === 'in' ? 'Прихід' : 'Вибуття',
        'Кількість': t.quantity,
        'Одиниця': si.unit || '—',
        'Ціна за од.': t.unit_price ?? '—',
        'Вартість': t.total_amount ?? '—',
        'Коментар': t.comment || '—',
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Операції');
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const dateStr = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}`;
    const timeStr = `${pad(now.getHours())}-${pad(now.getMinutes())}`;
    const warehouseName = (STOCK_KIND_TITLES[stockKind] || 'Склад').replace(/\s+/g, '_');
    XLSX.writeFile(wb, `${warehouseName}_операції_${dateStr}_${timeStr}.xlsx`);
  };

  const categoryFilter = STOCK_KIND_CATEGORY[stockKind] || null;
  const getItemCategory = (item) =>
    item.item_category ||
    (item.raw_material ? 'raw_material' : item.dye ? 'dye' : item.packaging_material ? 'packaging' : item.product ? 'product' : item.consumable_material ? 'consumable' : null);

  const rawMaterialsForDialog = useMemo(() => {
    const notRemelt = (m) =>
      m?.type !== 'Перемол' && m?.type !== 'перемол' && !String(m?.article || '').toUpperCase().startsWith('PEREMOL-');
    let list = rawMaterials.filter(notRemelt);
    if (!stockKind || isProductWarehouse) return list;
    if (stockKind === 'raw') {
      return list.filter((m) => (m.store_category || 'raw') !== 'technical');
    }
    if (stockKind === 'technical') {
      return list.filter((m) => m.store_category === 'technical');
    }
    return list;
  }, [rawMaterials, stockKind, isProductWarehouse]);

  const matchesSearch = (article, name) => {
    if (searchTokens.length === 0) return true;
    const a = typeof article === 'string' ? article.toLowerCase() : '';
    const n = typeof name === 'string' ? name.toLowerCase() : '';
    return searchTokens.every((t) => a.includes(t) || n.includes(t));
  };

  const cmpStr = (a, b) => String(a || '').localeCompare(String(b || ''), 'uk', { sensitivity: 'base' });
  const cmpStrNatural = (a, b) =>
    String(a || '').localeCompare(String(b || ''), 'uk', { numeric: true, sensitivity: 'base' });
  const cmpNum = (a, b) => (Number(a || 0) || 0) - (Number(b || 0) || 0);
  const applyQtyFilter = (items, getQty) => {
    if (qtyFilter === 'gt0') return items.filter((x) => Number(getQty(x) || 0) > 0);
    if (qtyFilter === 'eq0') return items.filter((x) => Number(getQty(x) || 0) === 0);
    if (qtyFilter === 'lt0') return items.filter((x) => Number(getQty(x) || 0) < 0);
    return items;
  };

  const sortSign = sortDir === 'desc' ? -1 : 1;

  const filteredStockItems = useMemo(() => {
    // Перемол — лише на Складі перемолів (навіть якщо API ще віддав legacy PEREMOL-*).
    let items = stockItems.filter((item) => !isRemeltMaterialItem(item));
    if (isProductWarehouse) {
      items = items.filter((item) => getItemCategory(item) === 'product');
    } else if (stockKind === 'raw') {
      items = items.filter(
        (item) => getItemCategory(item) === 'raw_material' && rawStoreSlotFromItem(item) === 'raw',
      );
    } else if (stockKind === 'technical') {
      items = items.filter(
        (item) =>
          getItemCategory(item) === 'raw_material' && rawStoreSlotFromItem(item) === 'technical',
      );
    } else if (stockKind === 'consumable') {
      items = items.filter((item) => getItemCategory(item) === 'consumable');
    } else if (categoryFilter) {
      items = items.filter((item) => getItemCategory(item) === categoryFilter);
    }
    if (normalizedSearchQuery) {
      items = items.filter((item) =>
        matchesSearch(getItemArticle(item), getItemDisplayName(item)),
      );
    }
    items = applyQtyFilter(items, (x) => x.quantity);
    const sorted = [...items].sort((a, b) => {
      let c = 0;
      if (sortKey === 'article') c = cmpStrNatural(getItemArticle(a), getItemArticle(b));
      else if (sortKey === 'quantity') c = cmpNum(a.quantity, b.quantity);
      else if (sortKey === 'updated') c = cmpStr(a.updated_at, b.updated_at);
      else c = cmpStr(getItemDisplayName(a), getItemDisplayName(b));
      if (c !== 0) return c * sortSign;
      // tie-breaker
      return cmpNum(a.id, b.id) * sortSign;
    });
    return sorted;
  }, [
    stockItems,
    stockKind,
    categoryFilter,
    normalizedSearchQuery,
    isProductWarehouse,
    qtyFilter,
    sortKey,
    sortDir,
  ]);

  const stockAvailableQty = (si) => {
    if (!si) return 0;
    const avail = si.available_quantity;
    if (avail != null && Number.isFinite(Number(avail))) return Number(avail);
    return Number(si.quantity) || 0;
  };

  /** Позиції поточного складу із доступним залишком > 0 (переміщення / вибуття). */
  const stockItemsWithBalance = useMemo(() => {
    let items = stockItems.filter((item) => !isRemeltMaterialItem(item));
    if (isProductWarehouse) {
      items = items.filter((item) => getItemCategory(item) === 'product');
    } else if (stockKind === 'raw') {
      items = items.filter(
        (item) => getItemCategory(item) === 'raw_material' && rawStoreSlotFromItem(item) === 'raw',
      );
    } else if (stockKind === 'technical') {
      items = items.filter(
        (item) =>
          getItemCategory(item) === 'raw_material' && rawStoreSlotFromItem(item) === 'technical',
      );
    } else if (stockKind === 'consumable') {
      items = items.filter((item) => getItemCategory(item) === 'consumable');
    } else if (categoryFilter) {
      items = items.filter((item) => getItemCategory(item) === categoryFilter);
    }
    return items.filter((si) => stockAvailableQty(si) > 0);
  }, [stockItems, stockKind, categoryFilter, isProductWarehouse]);

  /** Матеріали для модалки переміщення: лише активна категорія складу + залишок > 0. */
  const transferMaterialOptions = stockItemsWithBalance;

  const outSelectedStockItem = useMemo(() => {
    if (formData.transaction_type !== 'out') return null;
    if (formData.item_category === 'raw_material' && formData.raw_material) {
      return stockItemsWithBalance.find((si) => si.raw_material === Number(formData.raw_material) || si.raw_material === formData.raw_material) || null;
    }
    if (formData.item_category === 'dye' && formData.dye) {
      return stockItemsWithBalance.find((si) => si.dye === Number(formData.dye) || si.dye === formData.dye) || null;
    }
    if (formData.item_category === 'packaging' && formData.packaging_material) {
      return stockItemsWithBalance.find(
        (si) => si.packaging_material === Number(formData.packaging_material) || si.packaging_material === formData.packaging_material,
      ) || null;
    }
    if (formData.item_category === 'consumable' && formData.consumable_material) {
      return stockItemsWithBalance.find(
        (si) => si.consumable_material === Number(formData.consumable_material) || si.consumable_material === formData.consumable_material,
      ) || null;
    }
    return null;
  }, [formData, stockItemsWithBalance]);

  const outMaxQty = outSelectedStockItem ? stockAvailableQty(outSelectedStockItem) : null;

  const outRawMaterials = useMemo(() => {
    const ids = new Set(
      stockItemsWithBalance
        .filter((si) => getItemCategory(si) === 'raw_material')
        .map((si) => si.raw_material),
    );
    return rawMaterialsForDialog.filter((m) => ids.has(m.id));
  }, [rawMaterialsForDialog, stockItemsWithBalance]);

  const outDyes = useMemo(() => {
    const ids = new Set(
      stockItemsWithBalance.filter((si) => getItemCategory(si) === 'dye').map((si) => si.dye),
    );
    return dyes.filter((d) => ids.has(d.id));
  }, [dyes, stockItemsWithBalance]);

  const outPackaging = useMemo(() => {
    const ids = new Set(
      stockItemsWithBalance
        .filter((si) => getItemCategory(si) === 'packaging')
        .map((si) => si.packaging_material),
    );
    return packagingMaterials.filter((p) => ids.has(p.id));
  }, [packagingMaterials, stockItemsWithBalance]);

  const outConsumables = useMemo(() => {
    const ids = new Set(
      stockItemsWithBalance
        .filter((si) => getItemCategory(si) === 'consumable')
        .map((si) => si.consumable_material),
    );
    return consumableMaterials.filter((c) => ids.has(c.id));
  }, [consumableMaterials, stockItemsWithBalance]);

  const filteredTransactions = useMemo(() => {
    let items = transactions;
    if (isProductWarehouse) {
      items = items.filter((t) => t.stock_item_data?.item_category === 'product');
    } else if (stockKind === 'raw') {
      items = items.filter((t) => {
        const d = t.stock_item_data;
        return (
          d?.item_category === 'raw_material' &&
          rawStoreSlotFromNested(d) === 'raw' &&
          !isRemeltMaterialNested(d)
        );
      });
    } else if (stockKind === 'technical') {
      items = items.filter((t) => {
        const d = t.stock_item_data;
        return (
          d?.item_category === 'raw_material' &&
          rawStoreSlotFromNested(d) === 'technical' &&
          !isRemeltMaterialNested(d)
        );
      });
    } else if (stockKind === 'consumable') {
      items = items.filter((t) => t.stock_item_data?.item_category === 'consumable');
    } else if (categoryFilter) {
      items = items.filter((t) => {
        const cat = t.stock_item_data?.item_category;
        return cat === categoryFilter;
      });
    }
    if (normalizedSearchQuery) {
      items = items.filter((t) => {
        const d = t.stock_item_data;
        if (!d) return false;
        const cat = d.item_category;
        if (cat === 'raw_material') return matchesSearch(d.raw_material_article || '', d.raw_material_name || '');
        if (cat === 'dye') return matchesSearch(d.dye_article || '', d.dye_name || '');
        if (cat === 'packaging') return matchesSearch(d.packaging_material_article || '', d.packaging_material_name || '');
        if (cat === 'product') return matchesSearch(d.product_article || '', d.product_name || '');
        if (cat === 'consumable') return matchesSearch(d.consumable_material_article || '', d.consumable_material_name || '');
        return matchesSearch(d.raw_material_article || '', d.raw_material_name || '');
      });
    }
    if (txTypeFilter !== 'all') {
      items = items.filter((t) => t.transaction_type === txTypeFilter);
    }
    if (txDateFrom) {
      const from = new Date(txDateFrom);
      items = items.filter((t) => t.created_at && new Date(t.created_at) >= from);
    }
    if (txDateTo) {
      const to = new Date(txDateTo);
      to.setHours(23, 59, 59, 999);
      items = items.filter((t) => t.created_at && new Date(t.created_at) <= to);
    }
    const sorted = [...items].sort((a, b) => {
      const c = cmpStr(a.created_at, b.created_at);
      return (c !== 0 ? c : cmpNum(a.id, b.id)) * -1; // newest first
    });
    return sorted;
  }, [transactions, stockKind, categoryFilter, normalizedSearchQuery, isProductWarehouse, txTypeFilter, txDateFrom, txDateTo]);

  const filteredArrivals = useMemo(() => {
    let items = arrivals;
    // Перемол після дроблення — лише на Складі перемолів (StockCrushingWastePage).
    items = items.filter((a) => !isCrushingRemeltArrival(a));
    if (isProductWarehouse) {
      items = items.filter((a) => {
        const d = a.stock_item_data || {};
        const isProduct =
          d.item_category === 'product' || (!a.purchase && !d?.item_category);
        if (!isProduct) return false;
        const c = String(a.comment || '');
        const toPackaging =
          c.includes('Склад для пакування') || /упаковк/iu.test(c);
        const toSemi =
          c.includes('Склад напівфабрикатів') || /напівфабрик/iu.test(c);
        if (stockKind === 'packaging_work') {
          // Legacy «Виробництво #N:» без складу — це пакування; явні напівфабрикати відсікаємо
          if (toSemi) return false;
          return toPackaging || c.includes('Виробництво #') || Boolean(a.packaging_task);
        }
        if (stockKind === 'semi') {
          return toSemi || (!toPackaging && !a.packaging_task && !c.includes('Виробництво #'));
        }
        return true;
      });
    } else if (stockKind === 'raw') {
      items = items.filter((a) => {
        const d = a.stock_item_data;
        return (
          d?.item_category === 'raw_material' &&
          rawStoreSlotFromNested(d) === 'raw' &&
          !isRemeltMaterialNested(d)
        );
      });
    } else if (stockKind === 'technical') {
      items = items.filter((a) => {
        const d = a.stock_item_data;
        return (
          d?.item_category === 'raw_material' &&
          rawStoreSlotFromNested(d) === 'technical' &&
          !isRemeltMaterialNested(d)
        );
      });
    } else if (categoryFilter) {
      items = items.filter((a) => {
        const cat = a.stock_item_data?.item_category;
        return cat === categoryFilter;
      });
    }
    if (normalizedSearchQuery) {
      items = items.filter((a) => {
        const d = a.stock_item_data || {};
        const hasMaterialName = d?.raw_material_name || d?.dye_name || d?.packaging_material_name;
        const isProductItem = d?.item_category === 'product';
        const isSemiFromProductionLegacy = !a.purchase && !d?.item_category;

        const parsedLegacyName = (() => {
          const c = a.comment || '';
          if (typeof c !== 'string' || !c.trim()) return '-';
          return c
            .replace(/^Напівфабрикат:\s*/u, '')
            .replace(/\s*x\s*[\d.,]+\s*\(замовлення\s*#\d+\)\s*$/u, '')
            .replace(/\s*x\s*[\d.,].*$/u, '')
            .trim();
        })();

        if (isProductItem) {
          return matchesSearch(d.product_article || '', d.product_name || '');
        }
        if (isSemiFromProductionLegacy) {
          return matchesSearch('', parsedLegacyName || '-');
        }
        if (d?.item_category === 'raw_material') return matchesSearch(d.raw_material_article || '', d.raw_material_name || '');
        if (d?.item_category === 'dye') return matchesSearch(d.dye_article || '', d.dye_name || '');
        if (d?.item_category === 'packaging') return matchesSearch(d.packaging_material_article || '', d.packaging_material_name || '');
        if (hasMaterialName) return matchesSearch(d.raw_material_article || '', d.raw_material_name || '');
        return false;
      });
    }
    const sorted = [...items].sort((a, b) => {
      const c = cmpStr(a.created_at, b.created_at);
      return (c !== 0 ? c : cmpNum(a.id, b.id)) * -1; // newest first
    });
    return sorted;
  }, [arrivals, stockKind, categoryFilter, normalizedSearchQuery, isProductWarehouse]);

  const getItemTypeLabel = (item) => {
    if (item.item_category === 'raw_material') return 'Сировина';
    if (item.item_category === 'dye') return 'Барвник';
    if (item.item_category === 'packaging') return 'Упаковка';
    if (item.item_category === 'product') return 'Виріб';
    if (item.item_category === 'consumable') return 'Розхідний матеріал';
    return item.raw_material ? 'Сировина' : item.dye ? 'Барвник' : item.packaging_material ? 'Упаковка' : 'Виріб';
  };

  const getTransactionName = (t) => {
    const d = t.stock_item_data;
    if (!d) return '-';
    if (d.item_category === 'raw_material') {
      return formatArticleName(d.raw_material_article, d.raw_material_name);
    }
    if (d.item_category === 'dye') {
      return formatArticleName(d.dye_article, d.dye_name);
    }
    if (d.item_category === 'packaging') {
      return formatArticleName(d.packaging_material_article, d.packaging_material_name);
    }
    if (d.item_category === 'product') {
      return formatArticleName(d.product_article, d.product_name);
    }
    if (d.item_category === 'consumable') {
      return formatArticleName(d.consumable_material_article, d.consumable_material_name);
    }
    return formatArticleName(d.raw_material_article, d.raw_material_name);
  };

  const arrivalsBadgeCount = filteredArrivals.length;

  const loadRequests = (tab) => {
    const status = tab === 0 ? 'new' : 'transferred';
    setRequestsLoading(true);
    api.get('/stock-material-requests/', { params: { status, ...requestsQueryParams } })
      .then(r => {
        const d = r.data?.results ?? r.data ?? [];
        const list = Array.isArray(d) ? d : [];
        setRequests(list);
        if (status === 'new') setRequestsBadge(list.length);
      })
      .catch(() => setRequests([]))
      .finally(() => setRequestsLoading(false));
  };

  const handleOpenRequests = () => {
    setShowRequests(true);
    loadRequests(requestsTab);
  };

  const handleRequestsTabChange = (_, newVal) => {
    setRequestsTab(newVal);
    loadRequests(newVal);
  };

  const handleTransferRequest = async () => {
    const req = transferReqDialog;
    if (!req) return;
    const qty = parseFloat(transferReqQty);
    if (!Number.isFinite(qty) || qty <= 0) { alert('Вкажіть коректну кількість'); return; }
    if (!req.stock_item_id) { alert('Не знайдено позицію на складі для цього матеріалу'); return; }
    setTransferReqLoading(true);
    try {
      await api.post('/production-warehouse/transfer-in/', {
        stock_item: req.stock_item_id,
        quantity: qty,
        note: `Заявка #${req.id} від ${req.requester_name}`
          + (req.production_order_ref ? ` для ${req.production_order_ref}` : ''),
        // Резерв знімається саме з цього замовлення, а рух СВ привʼязується до нього
        ...(req.production_order ? { production_order: req.production_order } : {}),
      });
      await api.post(`/stock-material-requests/${req.id}/transfer/`, {
        transferred_by: getCurrentUserName(),
        quantity: qty,
      });
      setTransferReqDialog(null);
      setTransferReqQty('');
      loadRequests(requestsTab);
      refreshRequestsBadge();
    } catch (e) {
      alert('Помилка: ' + (e?.response?.data?.error || e?.response?.data?.detail || e.message));
    } finally {
      setTransferReqLoading(false);
    }
  };

  const handleOpenArrivalDialog = (arrival) => {
    setSelectedArrival(arrival);
    setArrivalActualQty(
      arrival.quantity_actual != null
        ? String(arrival.quantity_actual)
        : String(arrival.quantity_planned ?? '')
    );
    setArrivalDialogOpen(true);
  };

  const handleConfirmArrival = async () => {
    if (!selectedArrival) return;
    if (isCrushingRemeltArrival(selectedArrival)) {
      alert('Надходження перемолу приймається лише на Складі перемолів.');
      return;
    }
    const qty = parseFloat(arrivalActualQty);
    if (!Number.isFinite(qty) || qty <= 0) {
      alert('Вкажіть коректну кількість');
      return;
    }
    try {
      await stockArrivalService.confirm(selectedArrival.id, { quantity_actual: qty });
      setArrivalDialogOpen(false);
      setSelectedArrival(null);
      setArrivalActualQty('');
      loadData();
    } catch (error) {
      console.error('Помилка підтвердження надходження:', error);
      const msg =
        error.response?.data &&
        (typeof error.response.data === 'string'
          ? error.response.data
          : JSON.stringify(error.response.data, null, 2));
      alert(`Помилка підтвердження:\n${msg || error.message || 'Невідома помилка'}`);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ flexShrink: 0, px: 2, pt: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">
          {stockKind ? STOCK_KIND_TITLES[stockKind] : 'Склад матеріалів'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <EntityImportExportButtons
            entityKey="stock_items"
            label="Склад_залишки"
            showImport={false}
            stockKind={stockKind}
          />
          <Button
            variant={showRequests ? 'contained' : 'outlined'}
            color="secondary"
            startIcon={<ShoppingCartIcon />}
            onClick={() => {
              if (showRequests) { setShowRequests(false); } else { handleOpenRequests(); }
            }}
          >
            <Badge badgeContent={requestsBadge} color="error" sx={{ '& .MuiBadge-badge': { right: -10, top: -6 } }}>
              Заявки
            </Badge>
          </Button>
          <Button
            variant={shortageOpen ? 'contained' : 'outlined'}
            color="warning"
            startIcon={<WarningAmberIcon />}
            onClick={() => {
              if (!shortageOpen) {
                setShortageLoading(true);
                api.get('/material-shortages/')
                  .then(r => { const d = r.data || []; setShortages(d); setShortageBadge(d.length); })
                  .catch(() => setShortages([]))
                  .finally(() => setShortageLoading(false));
              }
              setShortageOpen(o => !o);
            }}
          >
            <Badge badgeContent={shortageBadge} color="error" sx={{ '& .MuiBadge-badge': { right: -10, top: -6 } }}>
              Нестача
            </Badge>
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<MenuBookIcon />}
            onClick={(e) => setRefsAnchorEl(e.currentTarget)}
          >
            Довідники
          </Button>
          <Menu
            anchorEl={refsAnchorEl}
            open={refsMenuOpen}
            onClose={() => setRefsAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {[
              ['/raw-materials', 'Сировина'],
              ['/dyes', 'Фарби'],
              ['/packaging-materials', 'Упаковка'],
              ['/filament', 'Філамент'],
              ['/consumable-materials', 'Розхідні матеріали'],
            ].map(([path, label]) => (
              <MenuItem
                key={path}
                onClick={() => {
                  setRefsAnchorEl(null);
                  const tab = stockKind === 'packaging_work' ? 'packaging-work' : (stockKind || 'raw');
                  navigateWithReturn(navigate, path, stockReturnTo(tab), stockReturnLabel(tab));
                }}
              >
                {label}
              </MenuItem>
            ))}
          </Menu>
          <Button
            variant="contained"
            color="success"
            startIcon={<InputIcon />}
            onClick={() => handleOpen('in')}
            sx={{ mr: 1 }}
          >
            Прихід
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<OutputIcon />}
            onClick={() => handleOpen('out')}
            sx={{ mr: 1 }}
          >
            Вибуття
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<SwapHorizIcon />}
            onClick={() => setTransferHubOpen(true)}
          >
            Переміщення
          </Button>
        </Box>
      </Box>

      {stockKind === 'packaging_work' && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Пакування (з планера)
          </Typography>
          <TableContainer component={Paper}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Виріб</TableCell>
                  <TableCell>К-сть</TableCell>
                  <TableCell>План старт</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Пакування</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {packagingRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="textSecondary">Немає заявок</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  packagingRequests.map((o) => (
                    <TableRow key={o.id}>
                      <TableCell>{o.id}</TableCell>
                      <TableCell>{o.product_name || '—'}</TableCell>
                      <TableCell>{o.quantity}</TableCell>
                      <TableCell>
                        {o.planned_start ? String(o.planned_start).replace('T', ' ').slice(0, 16) : '—'}
                      </TableCell>
                      <TableCell>{o.status_display || o.status}</TableCell>
                      <TableCell>{o.packaging_note || '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Пошук по артикулу або назві"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ flex: '1 1 340px' }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="stock-qty-filter-label">Фільтр</InputLabel>
            <Select
              labelId="stock-qty-filter-label"
              label="Фільтр"
              value={qtyFilter}
              onChange={(e) => setQtyFilter(e.target.value)}
            >
              <MenuItem value="all">Усі</MenuItem>
              <MenuItem value="gt0">Тільки &gt; 0</MenuItem>
              <MenuItem value="eq0">Тільки = 0</MenuItem>
              <MenuItem value="lt0">Тільки &lt; 0</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="stock-sort-key-label">Сортування</InputLabel>
            <Select
              labelId="stock-sort-key-label"
              label="Сортування"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
            >
              <MenuItem value="name">За назвою</MenuItem>
              <MenuItem value="article">За артикулом</MenuItem>
              <MenuItem value="quantity">За кількістю</MenuItem>
              <MenuItem value="updated">За датою оновлення</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="stock-sort-dir-label">Напрям</InputLabel>
            <Select
              labelId="stock-sort-dir-label"
              label="Напрям"
              value={sortDir}
              onChange={(e) => setSortDir(e.target.value)}
            >
              <MenuItem value="asc">↑ Зростання</MenuItem>
              <MenuItem value="desc">↓ Спадання</MenuItem>
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setSearchQuery('');
              setQtyFilter('all');
              setSortKey('name');
              setSortDir('asc');
            }}
          >
            Скинути
          </Button>
        </Box>
        {(showRequests || shortageOpen) && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
            {showRequests && (
              <Chip
                label="Активний фільтр: Заявки"
                color="secondary"
                onDelete={() => setShowRequests(false)}
                sx={{ fontWeight: 600 }}
              />
            )}
            {shortageOpen && (
              <Chip
                label="Активний фільтр: Нестача"
                color="warning"
                onDelete={() => setShortageOpen(false)}
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>
        )}
      </Box>

      {/* ── Панель нестач ───────────────────────────────────────── */}
      <Collapse in={shortageOpen}>
        <Box sx={{ mb: 3, border: '1px solid', borderColor: 'warning.main', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1, bgcolor: 'warning.main', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'warning.contrastText' }}>
              ⚠️ Нестача матеріалів для запланованих виробничих замовлень
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Tooltip title="Оновити">
                <IconButton size="small" onClick={() => {
                  setShortageLoading(true);
                  api.get('/material-shortages/')
                    .then(r => setShortages(r.data || []))
                    .catch(() => {})
                    .finally(() => setShortageLoading(false));
                }} sx={{ color: 'warning.contrastText' }}>
                  {shortageLoading ? <CircularProgress size={16} color="inherit" /> : <ExpandLessIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          {shortageLoading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={28} /></Box>
          ) : shortages.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="success.main" fontWeight={600}>✅ Нестач немає — всіх матеріалів достатньо</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Артикул</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Назва</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Тип</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Початок вир-ва</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Дедлайн</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>На складі</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'error.main' }}>Не вистачає</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>В закупівлі</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Дата закупівлі</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shortages.map((row) => {
                    const typeLabel = row.mat_type === 'raw' ? 'Сировина' : row.mat_type === 'dye' ? 'Барвник' : 'Упаковка';
                    const typeColor = row.mat_type === 'raw' ? 'primary' : row.mat_type === 'dye' ? 'secondary' : 'warning';
                    const afterPurchase = row.available + row.in_purchase - row.needed;
                    const coveredByPurchase = afterPurchase >= 0;
                    return (
                      <TableRow key={row.stock_item_id} hover sx={{ bgcolor: coveredByPurchase ? 'transparent' : undefined }}>
                        <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{row.article || '—'}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', fontWeight: 500 }}>{row.name}</TableCell>
                        <TableCell>
                          <Chip label={typeLabel} color={typeColor} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {row.planned_start ? new Date(row.planned_start).toLocaleDateString('uk-UA') : '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: row.deadline ? 600 : 400, color: row.deadline ? 'error.main' : 'text.secondary' }}>
                          {row.deadline ? new Date(row.deadline).toLocaleDateString('uk-UA') : '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {row.available.toFixed(2)}
                          {row.reserved > 0 && <Typography component="span" variant="caption" color="text.secondary"> (+{row.reserved.toFixed(2)} рез)</Typography>}
                          <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.disabled' }}>{row.unit}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: coveredByPurchase ? 'warning.main' : 'error.main' }}>
                            {row.shortage.toFixed(2)} {row.unit}
                          </Typography>
                          {coveredByPurchase && (
                            <Typography variant="caption" color="success.main">покрито закупівлею</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', color: row.in_purchase > 0 ? 'success.main' : 'text.secondary' }}>
                          {row.in_purchase > 0 ? `${row.in_purchase.toFixed(2)} ${row.unit}` : '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {row.purchase_dates.length > 0
                            ? row.purchase_dates.map(d => new Date(d).toLocaleDateString('uk-UA')).join(', ')
                            : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Collapse>

      {!showRequests && <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Залишки" />
          <Tab label="Операції" />
          <Tab
            label={
              <Badge color="error" badgeContent={arrivalsBadgeCount || 0}>
                Надходження
              </Badge>
            }
          />
        </Tabs>
        {tabValue === 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2, flexWrap: 'wrap', flex: 1 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Тип операції</InputLabel>
              <Select
                label="Тип операції"
                value={txTypeFilter}
                onChange={(e) => setTxTypeFilter(e.target.value)}
              >
                <MenuItem value="all">Усі</MenuItem>
                <MenuItem value="in">Прихід</MenuItem>
                <MenuItem value="out">Вибуття</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small" type="date" label="З"
              value={txDateFrom}
              onChange={(e) => setTxDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150 }}
            />
            <TextField
              size="small" type="date" label="По"
              value={txDateTo}
              onChange={(e) => setTxDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150 }}
            />
            {(txDateFrom || txDateTo) && (
              <Button size="small" onClick={() => { setTxDateFrom(''); setTxDateTo(''); }}
                sx={{ textTransform: 'none', color: '#999' }}>
                Скинути
              </Button>
            )}
            <Box sx={{ ml: 'auto' }}>
              <Button
                variant="outlined" size="small"
                onClick={handleExportTransactions}
                disabled={filteredTransactions.length === 0}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                ⬇ Експорт ({filteredTransactions.length})
              </Button>
            </Box>
          </Box>
        )}
      </Box>}
      </Box>{/* end flexShrink:0 controls */}

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 2, pb: 2 }}>

      {/* ===== ЗАЯВКИ панель ===== */}
      {showRequests && (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Tabs value={requestsTab} onChange={handleRequestsTabChange}>
              <Tab label="Нові" />
              <Tab label="Історія" />
            </Tabs>
          </Box>
          {requestsLoading ? (
            <Box display="flex" justifyContent="center" pt={4}><CircularProgress /></Box>
          ) : (
            <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>№</TableCell>
                    <TableCell>Хто</TableCell>
                    <TableCell>Тип</TableCell>
                    <TableCell>Артикул</TableCell>
                    <TableCell>Назва</TableCell>
                    <TableCell align="right">Потрібно</TableCell>
                    <TableCell align="right">Переміщено</TableCell>
                    <TableCell align="right">Залишок</TableCell>
                    <TableCell align="right">На складі</TableCell>
                    <TableCell align="right">Вільний залишок</TableCell>
                    {requestsTab === 1 && <TableCell>Дата/час переміщень</TableCell>}
                    {requestsTab === 1 && <TableCell>Хто перемістив</TableCell>}
                    {requestsTab === 0 && <TableCell></TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={requestsTab === 0 ? 10 : 11} align="center">
                        <Typography color="textSecondary">Немає заявок</Typography>
                      </TableCell>
                    </TableRow>
                  ) : requests.map((req, idx) => {
                    const transferred = req.transferred_quantity ?? 0;
                    const remaining = Math.max(0, req.quantity - transferred);
                    const catLabels = { raw_material: 'Сировина', dye: 'Барвник', packaging: 'Упаковка', packaging_material: 'Упаковка', consumable_material: 'Розхідні' };
                    return (
                    <TableRow key={req.id}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        {req.requester_name}
                        {req.production_order_ref && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {`для ${req.production_order_ref}`}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{catLabels[req.item_category] || req.item_category}</TableCell>
                      <TableCell>{req.article || '—'}</TableCell>
                      <TableCell>{req.name || req.packaging_material_name || '—'}</TableCell>
                      <TableCell align="right">{fmtQty(req.quantity)}</TableCell>
                      <TableCell align="right" sx={{ color: transferred > 0 ? 'success.main' : 'text.secondary', fontWeight: transferred > 0 ? 600 : 400 }}>
                        {transferred > 0 ? transferred.toFixed(3) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: remaining > 0 ? 'warning.main' : 'success.main', fontWeight: 600 }}>
                        {remaining > 0 ? remaining.toFixed(3) : '✓'}
                      </TableCell>
                      <TableCell align="right">{req.stock_balance != null ? fmtQty(req.stock_balance) : '—'}</TableCell>
                      <TableCell align="right">{req.available_balance != null ? fmtQty(req.available_balance) : '—'}</TableCell>
                      {requestsTab === 1 && (
                        <TableCell sx={{ fontSize: '0.75rem' }}>
                          {(req.transfer_logs || []).length > 0
                            ? req.transfer_logs.map((l, i) => (
                                <div key={i}>{new Date(l.transferred_at).toLocaleString('uk-UA')} — {l.quantity} {unitForCategory(req.item_category || 'packaging')}</div>
                              ))
                            : (req.transferred_at ? new Date(req.transferred_at).toLocaleString('uk-UA') : '—')}
                        </TableCell>
                      )}
                      {requestsTab === 1 && (
                        <TableCell sx={{ fontSize: '0.75rem' }}>
                          {(req.transfer_logs || []).length > 0
                            ? req.transfer_logs.map((l, i) => <div key={i}>{l.transferred_by}</div>)
                            : (req.transferred_by || '—')}
                        </TableCell>
                      )}
                      {requestsTab === 0 && (
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            disabled={transferReqLoading || remaining <= 0}
                            onClick={() => { setTransferReqDialog(req); setTransferReqQty(String(remaining.toFixed(3))); }}
                          >
                            Перемістити
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {!showRequests && tabValue === 0 && (
        <TableContainer component={Paper} sx={{ height: '100%' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Артикул</TableCell>
                <TableCell>Назва</TableCell>
                <TableCell>Кількість</TableCell>
                <TableCell sx={{ color: '#e65100', fontWeight: 600 }}>Зарезервовано</TableCell>
                <TableCell sx={{ color: '#2e7d32', fontWeight: 600 }}>Доступно</TableCell>
                <TableCell>Одиниця</TableCell>
                <TableCell>Вартість</TableCell>
                <TableCell>Оновлено</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredStockItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="textSecondary">Немає залишків на складі</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredStockItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{getItemArticle(item)}</TableCell>
                    <TableCell>{getItemDisplayName(item)}</TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="bold"
                        color={item.quantity < 10 ? 'error' : 'inherit'}>
                        {item.quantity}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {item.reserved_quantity > 0 ? (
                        <Typography variant="body2" fontWeight={600} color="#e65100">
                          {item.reserved_quantity}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}
                        color={item.available_quantity < 10 ? '#c62828' : '#2e7d32'}>
                        {item.available_quantity ?? item.quantity}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.unit}</TableCell>
                    <TableCell>
                      {item.total_value != null ? `${item.total_value} грн` : '—'}
                    </TableCell>
                    <TableCell>
                      {new Date(item.updated_at).toLocaleString('uk-UA')}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!showRequests && tabValue === 2 && (
        <TableContainer component={Paper}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Дата</TableCell>
                <TableCell>Матеріал</TableCell>
                <TableCell>Тип</TableCell>
                <TableCell>Кількість (план)</TableCell>
                <TableCell>Кількість (факт)</TableCell>
                <TableCell>Джерело</TableCell>
                <TableCell align="right">Дії</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredArrivals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="textSecondary">
                      Немає очікуваних надходжень
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredArrivals.map((a) => {
                  const d = a.stock_item_data;
                  const hasMaterialName =
                    d?.raw_material_name || d?.dye_name || d?.packaging_material_name;
                  const isProductItem = d?.item_category === 'product';
                  // Legacy: раніше напівфабрикати з виробництва зберігались як StockItem без raw/dye/packaging,
                  // тому item_category було порожнє.
                  const isSemiFromProductionLegacy = !a.purchase && !d?.item_category;

                  const parsedLegacyName = (() => {
                    const c = a.comment || '';
                    if (typeof c !== 'string' || !c.trim()) return '-';
                    // Якщо коментар був у форматі "Напівфабрикат: X x10 (замовлення #Y)",
                    // показуємо тільки "X".
                    return c
                      .replace(/^Напівфабрикат:\s*/u, '')
                      .replace(/\s*x\s*[\d.,]+\s*\(замовлення\s*#\d+\)\s*$/u, '')
                      .replace(/\s*x\s*[\d.,].*$/u, '')
                      .trim();
                  })();

                  const name = isProductItem
                    ? formatArticleName(d?.product_article, d?.product_name) || parsedLegacyName || '-'
                    : isSemiFromProductionLegacy
                    ? parsedLegacyName
                    : hasMaterialName
                    ? d?.item_category === 'raw_material'
                      ? formatArticleName(d.raw_material_article, d.raw_material_name)
                      : d?.item_category === 'dye'
                      ? formatArticleName(d.dye_article, d.dye_name)
                      : d?.item_category === 'packaging'
                      ? formatArticleName(d.packaging_material_article, d.packaging_material_name)
                      : d.raw_material_name || d.dye_name || d.packaging_material_name
                    : '-';

                  const typeLabel = isProductItem || isSemiFromProductionLegacy
                    ? 'Виріб'
                    : d?.item_category === 'dye'
                    ? 'Барвник'
                    : d?.item_category === 'packaging'
                    ? 'Упаковка'
                    : 'Сировина';
                  return (
                    <TableRow key={a.id}>
                      <TableCell>
                        {a.created_at ? new Date(a.created_at).toLocaleString('uk-UA') : '—'}
                      </TableCell>
                      <TableCell>{name}</TableCell>
                      <TableCell>{typeLabel}</TableCell>
                      <TableCell>{a.quantity_planned}</TableCell>
                      <TableCell>{a.quantity_actual ?? '—'}</TableCell>
                      <TableCell>
                        {a.packaging_task
                          ? `Упаковка (задача №${a.packaging_task})`
                          : a.crushing_operation
                          ? `Перемол (дроблення №${a.crushing_operation})`
                          : a.purchase_status_display || '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleOpenArrivalDialog(a)}
                        >
                          Прийняти
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!showRequests && tabValue === 1 && (
        <>
        <TableContainer component={Paper}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Дата</TableCell>
                <TableCell>Категорія</TableCell>
                <TableCell>Назва</TableCell>
                <TableCell>Тип операції</TableCell>
                <TableCell>Кількість</TableCell>
                <TableCell>Ціна за од.</TableCell>
                <TableCell>Вартість</TableCell>
                <TableCell>Коментар</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTransactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="textSecondary">Немає операцій</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTransactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell>
                      {new Date(transaction.created_at).toLocaleString('uk-UA')}
                    </TableCell>
                    <TableCell>
                      {transaction.stock_item_data?.item_category === 'dye' ? 'Барвник' : transaction.stock_item_data?.item_category === 'packaging' ? 'Упаковка' : 'Сировина'}
                    </TableCell>
                    <TableCell>{getTransactionName(transaction)}</TableCell>
                    <TableCell>
                      <Chip
                        label={transaction.transaction_type_display}
                        color={transaction.transaction_type === 'in' ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{transaction.quantity}</TableCell>
                    <TableCell>
                      {transaction.unit_price != null ? `${transaction.unit_price} грн` : '—'}
                    </TableCell>
                    <TableCell>
                      {transaction.total_amount != null ? `${transaction.total_amount} грн` : '—'}
                    </TableCell>
                    <TableCell>{transaction.comment || '-'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </>
      )}
      </Box>{/* end flex:1 table area */}

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {formData.transaction_type === 'in' ? 'Прихід на склад' : 'Вибуття зі складу'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {!lockCategory && (
              <>
                <Typography variant="subtitle2" color="text.secondary">Тип матеріалу</Typography>
                <Tabs
                  value={formData.item_category}
                  onChange={(_, v) => {
                    priceHintReqRef.current += 1;
                    setPriceHint(null);
                    setFormData({
                      ...formData,
                      item_category: v,
                      raw_material: '',
                      dye: '',
                      packaging_material: '',
                      consumable_material: '',
                      unit_price: '',
                    });
                  }}
                  sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
                >
                  <Tab label="Сировина" value="raw_material" />
                  <Tab label="Барвник" value="dye" />
                  <Tab label="Упаковка" value="packaging" />
                  <Tab label="Розхідні" value="consumable" />
                </Tabs>
              </>
            )}

            {formData.item_category === 'raw_material' && (
              <FormControl fullWidth required sx={{ mb: 1 }}>
                <InputLabel id="raw-material-label" shrink>Сировина</InputLabel>
                <Select
                  labelId="raw-material-label"
                  value={formData.raw_material || ''}
                  onChange={(e) => {
                    const id = e.target.value;
                    applyRecommendedPrice('raw_material', id, {
                      raw_material: id,
                      dye: '',
                      packaging_material: '',
                      consumable_material: '',
                    });
                  }}
                  label="Сировина"
                  displayEmpty
                  MenuProps={SELECT_MENU_PROPS}
                >
                  <MenuItem value="">— оберіть —</MenuItem>
                  {(formData.transaction_type === 'out' ? outRawMaterials : rawMaterialsForDialog).map((material) => {
                    if (!material?.id) return null;
                    const stockItem = stockItems.find((i) => i.raw_material === material.id);
                    const avail = stockAvailableQty(stockItem);
                    const u = unitForCategory('raw_material', stockItem);
                    const stockInfo = formData.transaction_type === 'out'
                      ? ` — доступно: ${avail} ${u}`
                      : (stockItem ? ` — залишок: ${stockItem.quantity} ${u}` : ' — новий');
                    return (
                      <MenuItem key={material.id} value={material.id}>
                        {material.name} ({material.type}){stockInfo}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>
                  {formData.transaction_type === 'out'
                    ? (outRawMaterials.length ? `Доступно до списання: ${outRawMaterials.length}` : 'Немає позицій із залишком > 0')
                    : `Знайдено позицій: ${rawMaterialsForDialog.length}`}
                </FormHelperText>
              </FormControl>
            )}

            {formData.item_category === 'dye' && (
              <FormControl fullWidth required sx={{ mb: 1 }}>
                <InputLabel id="dye-label" shrink>Барвник</InputLabel>
                <Select
                  labelId="dye-label"
                  value={formData.dye || ''}
                  onChange={(e) => {
                    const id = e.target.value;
                    applyRecommendedPrice('dye', id, {
                      dye: id,
                      raw_material: '',
                      packaging_material: '',
                      consumable_material: '',
                    });
                  }}
                  label="Барвник"
                  displayEmpty
                  MenuProps={SELECT_MENU_PROPS}
                >
                  <MenuItem value="">— оберіть —</MenuItem>
                  {(formData.transaction_type === 'out' ? outDyes : dyes).map((d) => {
                    if (!d?.id) return null;
                    const stockItem = stockItems.find((i) => i.dye === d.id);
                    const avail = stockAvailableQty(stockItem);
                    const u = unitForCategory('dye', stockItem);
                    const stockInfo = formData.transaction_type === 'out'
                      ? ` — доступно: ${avail} ${u}`
                      : (stockItem ? ` — залишок: ${stockItem.quantity} ${u}` : ' — новий');
                    return (
                      <MenuItem key={d.id} value={d.id}>
                        {d.name}{d.color_code ? ` (${d.color_code})` : ''}{stockInfo}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>
                  {formData.transaction_type === 'out'
                    ? (outDyes.length ? `Доступно до списання: ${outDyes.length}` : 'Немає позицій із залишком > 0')
                    : `Знайдено барвників: ${dyes.length}`}
                </FormHelperText>
              </FormControl>
            )}

            {formData.item_category === 'packaging' && (
              <FormControl fullWidth required sx={{ mb: 1 }}>
                <InputLabel id="pack-label" shrink>Упаковка</InputLabel>
                <Select
                  labelId="pack-label"
                  value={formData.packaging_material || ''}
                  onChange={(e) => {
                    const id = e.target.value;
                    applyRecommendedPrice('packaging', id, {
                      packaging_material: id,
                      raw_material: '',
                      dye: '',
                      consumable_material: '',
                    });
                  }}
                  label="Упаковка"
                  displayEmpty
                  MenuProps={SELECT_MENU_PROPS}
                >
                  <MenuItem value="">— оберіть —</MenuItem>
                  {(formData.transaction_type === 'out' ? outPackaging : packagingMaterials).map((p) => {
                    if (!p?.id) return null;
                    const stockItem = stockItems.find((i) => i.packaging_material === p.id);
                    const avail = stockAvailableQty(stockItem);
                    const u = unitForCategory('packaging', stockItem, p);
                    const stockInfo = formData.transaction_type === 'out'
                      ? ` — доступно: ${avail} ${u}`
                      : (stockItem ? ` — залишок: ${stockItem.quantity} ${u}` : ' — новий');
                    return (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name} ({p.type || '—'}, {u}){stockInfo}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>
                  {formData.transaction_type === 'out'
                    ? (outPackaging.length ? `Доступно до списання: ${outPackaging.length}` : 'Немає позицій із залишком > 0')
                    : `Знайдено упаковки: ${packagingMaterials.length}`}
                </FormHelperText>
              </FormControl>
            )}

            {formData.item_category === 'consumable' && (
              <FormControl fullWidth required sx={{ mb: 1 }}>
                <InputLabel id="consumable-label" shrink>Розхідний матеріал</InputLabel>
                <Select
                  labelId="consumable-label"
                  value={formData.consumable_material || ''}
                  onChange={(e) => {
                    const id = e.target.value;
                    applyRecommendedPrice('consumable', id, {
                      consumable_material: id,
                      raw_material: '',
                      dye: '',
                      packaging_material: '',
                    });
                  }}
                  label="Розхідний матеріал"
                  displayEmpty
                  MenuProps={SELECT_MENU_PROPS}
                >
                  <MenuItem value="">— оберіть —</MenuItem>
                  {(formData.transaction_type === 'out' ? outConsumables : consumableMaterials).map((c) => {
                    if (!c?.id) return null;
                    const stockItem = stockItems.find((i) => i.consumable_material === c.id);
                    const avail = stockAvailableQty(stockItem);
                    const u = unitForCategory('consumable', stockItem);
                    const stockInfo = formData.transaction_type === 'out'
                      ? ` — доступно: ${avail} ${u}`
                      : (stockItem ? ` — залишок: ${stockItem.quantity} ${u}` : ' — новий');
                    return (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}{c.type ? ` (${c.type})` : ''}{stockInfo}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>
                  {formData.transaction_type === 'out'
                    ? (outConsumables.length ? `Доступно до списання: ${outConsumables.length}` : 'Немає позицій із залишком > 0')
                    : `Знайдено: ${consumableMaterials.length}`}
                </FormHelperText>
              </FormControl>
            )}
            {(() => {
              const selectedPack = formData.item_category === 'packaging'
                ? packagingMaterials.find((p) => String(p.id) === String(formData.packaging_material))
                : null;
              const formUnit = unitForCategory(
                formData.item_category,
                outSelectedStockItem,
                selectedPack,
              );
              const isPcs = formUnit === 'шт';
              return (
            <>
            <TextField
              label={`Кількість, ${formUnit}`}
              type="number"
              value={formData.quantity}
              onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
              fullWidth
              required
              inputProps={{
                min: isPcs ? 1 : 0.01,
                step: isPcs ? 1 : 0.01,
                ...(formData.transaction_type === 'out' && outMaxQty != null
                  ? { max: outMaxQty }
                  : {}),
              }}
              helperText={
                formData.transaction_type === 'out' && outSelectedStockItem
                  ? `Доступно (з урахуванням резерву): ${outMaxQty} ${formUnit}`.trim()
                  : undefined
              }
            />
            {formData.transaction_type === 'in' && (
              <>
                <TextField
                  label={`Ціна за ${formUnit} (грн)`}
                  type="number"
                  value={formData.unit_price}
                  onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                  fullWidth
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText={priceHint?.text || undefined}
                />
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Вартість: {(() => {
                    const q = parseFloat(formData.quantity);
                    const p = parseFloat(formData.unit_price);
                    if (Number.isFinite(q) && Number.isFinite(p) && q >= 0 && p >= 0) {
                      return `${(q * p).toFixed(2)} грн`;
                    }
                    return '—';
                  })()}
                </Typography>
              </>
            )}
            <TextField
              label="Коментар"
              multiline
              rows={3}
              value={formData.comment}
              onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
              fullWidth
            />
            </>
              );
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={txnSaving}>Скасувати</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color={formData.transaction_type === 'in' ? 'success' : 'error'}
            disabled={txnSaving}
          >
            {txnSaving ? <CircularProgress size={20} /> : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={arrivalDialogOpen}
        onClose={() => setArrivalDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Підтвердити надходження</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {selectedArrival?.stock_item_data?.raw_material_name ||
              selectedArrival?.stock_item_data?.dye_name ||
              selectedArrival?.stock_item_data?.packaging_material_name ||
              '-'}
          </Typography>
          <TextField
            label={`Кількість (факт), ${
              selectedArrival?.stock_item_data?.unit
              || unitForCategory(
                selectedArrival?.stock_item_data?.packaging_material
                  || selectedArrival?.stock_item_data?.packaging_material_name
                  ? 'packaging'
                  : (selectedArrival?.stock_item_data?.dye || selectedArrival?.stock_item_data?.dye_name
                    ? 'dye'
                    : (selectedArrival?.stock_item_data?.consumable_material || selectedArrival?.stock_item_data?.consumable_material_name
                      ? 'consumable'
                      : 'raw_material')),
              )
            }`}
            type="number"
            fullWidth
            margin="normal"
            value={arrivalActualQty}
            onChange={(e) => setArrivalActualQty(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArrivalDialogOpen(false)}>Скасувати</Button>
          <Button variant="contained" onClick={handleConfirmArrival}>
            Прийняти
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer hub dialog */}
      <Dialog open={transferHubOpen} onClose={() => setTransferHubOpen(false)} maxWidth="lg" fullWidth
        TransitionProps={{ onEnter: () => loadRequests(requestsTab) }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, pr: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: 1 }}>
            Переміщення матеріалів
            <Button variant="contained" color="warning" startIcon={<SwapHorizIcon />}
              onClick={() => {
                setTransferItem('');
                setTransferQty('');
                setTransferNote('');
                setTransferError('');
                setTransferDest(defaultTransferDest(transferDestOptions, sourceWarehouseId));
                setTransferOpen(true);
              }}>
              Ручне переміщення
            </Button>
          </Box>
          <IconButton edge="end" onClick={() => setTransferHubOpen(false)} aria-label="Закрити" size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ minHeight: 400, display: 'flex', flexDirection: 'column' }}>
          <Tabs value={requestsTab} onChange={(_, v) => { setRequestsTab(v); loadRequests(v); }} sx={{ mb: 2 }}>
            <Tab label="Нові заявки" />
            <Tab label="Історія" />
          </Tabs>
          {requestsLoading ? (
            <Box display="flex" justifyContent="center" pt={4}><CircularProgress /></Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ flex: 1 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>№</TableCell>
                    <TableCell>Хто замовив</TableCell>
                    <TableCell>Тип</TableCell>
                    <TableCell>Назва</TableCell>
                    <TableCell align="right">Потрібно</TableCell>
                    <TableCell align="right">Переміщено</TableCell>
                    <TableCell align="right">Залишок</TableCell>
                    <TableCell align="right">На складі</TableCell>
                    {requestsTab === 1 && <TableCell>Дата переміщень</TableCell>}
                    {requestsTab === 1 && <TableCell>Хто перемістив</TableCell>}
                    {requestsTab === 0 && <TableCell />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={requestsTab === 0 ? 9 : 10} align="center">
                        <Typography color="text.secondary" py={3}>Немає заявок</Typography>
                      </TableCell>
                    </TableRow>
                  ) : requests.map((req, idx) => {
                    const transferred = req.transferred_quantity ?? 0;
                    const remaining = Math.max(0, req.quantity - transferred);
                    const catLabels = { raw_material: 'Сировина', dye: 'Барвник', packaging: 'Упаковка', packaging_material: 'Упаковка', consumable_material: 'Розхідні' };
                    return (
                      <TableRow key={req.id} sx={{ bgcolor: remaining > 0 && requestsTab === 0 ? '#fff8f0' : 'inherit' }}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                        {req.requester_name}
                        {req.production_order_ref && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {`для ${req.production_order_ref}`}
                          </Typography>
                        )}
                      </TableCell>
                        <TableCell>{catLabels[req.item_category] || req.item_category}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{req.raw_material_name || req.dye_name || req.packaging_material_name || req.name || '—'}</TableCell>
                        <TableCell align="right">{req.quantity} {req.unit || unitForCategory(req.item_category)}</TableCell>
                        <TableCell align="right" sx={{ color: transferred > 0 ? 'success.main' : 'text.secondary', fontWeight: transferred > 0 ? 600 : 400 }}>
                          {transferred > 0 ? transferred.toFixed(3) : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: remaining > 0 ? 'warning.main' : 'success.main', fontWeight: 600 }}>
                          {remaining > 0 ? remaining.toFixed(3) : '✓'}
                        </TableCell>
                        <TableCell align="right">{req.stock_balance != null ? fmtQty(req.stock_balance) : '—'}</TableCell>
                        {requestsTab === 1 && (
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            {(req.transfer_logs || []).map((l, i) => (
                              <div key={i}>{new Date(l.transferred_at).toLocaleString('uk-UA')} — {l.quantity} {req.unit || unitForCategory(req.item_category)}</div>
                            ))}
                            {req.transferred_at && !req.transfer_logs?.length && new Date(req.transferred_at).toLocaleString('uk-UA')}
                          </TableCell>
                        )}
                        {requestsTab === 1 && (
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            {(req.transfer_logs || []).map((l, i) => <div key={i}>{l.transferred_by}</div>)}
                            {!req.transfer_logs?.length && (req.transferred_by || '—')}
                          </TableCell>
                        )}
                        {requestsTab === 0 && (
                          <TableCell>
                            <Button size="small" variant="contained" color="success"
                              disabled={transferReqLoading || remaining <= 0}
                              onClick={() => { setTransferReqDialog(req); setTransferReqQty(String(remaining.toFixed(3))); }}>
                              Перемістити
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferHubOpen(false)}>Закрити</Button>
        </DialogActions>
      </Dialog>

      {/* Transfer dialog */}
      <Dialog open={transferOpen} onClose={() => setTransferOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Переміщення матеріалу</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          {transferError && <Typography color="error">{transferError}</Typography>}
          <Autocomplete
            options={transferMaterialOptions}
            getOptionLabel={(si) => `${si.raw_material_name || si.dye_name || si.packaging_material_name || si.consumable_material_name || si.product_name || `#${si.id}`} — ${si.quantity} ${si.unit}`}
            value={transferMaterialOptions.find((si) => si.id === transferItem) || null}
            onChange={(_, val) => setTransferItem(val ? val.id : '')}
            renderInput={(params) => <TextField {...params} label="Матеріал (з поточного складу)" fullWidth />}
            ListboxProps={{ style: { maxHeight: 'min(40vh, 320px)' } }}
            filterOptions={(options, { inputValue }) => {
              const q = inputValue.toLowerCase();
              return options.filter((si) => {
                const name = (si.raw_material_name || si.dye_name || si.packaging_material_name || si.consumable_material_name || si.product_name || '').toLowerCase();
                return name.includes(q);
              });
            }}
            noOptionsText="Нічого не знайдено"
          />
          <TextField
            label={`Кількість, ${(transferMaterialOptions.find((si) => si.id === transferItem)?.unit) || unitForCategory(STOCK_KIND_CATEGORY[stockKind] || 'raw_material')}`}
            type="number"
            value={transferQty}
            onChange={(e) => setTransferQty(e.target.value)}
            fullWidth
            inputProps={{
              min: ((transferMaterialOptions.find((si) => si.id === transferItem)?.unit) || unitForCategory(STOCK_KIND_CATEGORY[stockKind] || 'raw_material')) === 'шт' ? 1 : 0.001,
              step: ((transferMaterialOptions.find((si) => si.id === transferItem)?.unit) || unitForCategory(STOCK_KIND_CATEGORY[stockKind] || 'raw_material')) === 'шт' ? 1 : 'any',
            }}
          />
          <TextField
            select
            label="Куди переміщуємо"
            value={transferDestOptions.some((w) => w.id === transferDest) ? transferDest : (transferDestOptions[0]?.id || '')}
            onChange={(e) => setTransferDest(e.target.value)}
            fullWidth
            SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
          >
            {transferDestOptions.map((w) => (
              <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Примітка"
            value={transferNote}
            onChange={(e) => setTransferNote(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferOpen(false)}>Скасувати</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!transferItem || !transferQty || transferLoading}
            onClick={async () => {
              setTransferLoading(true);
              setTransferError('');
              try {
                await api.post('/production-warehouse/transfer-in/', {
                  stock_item: transferItem,
                  quantity: parseFloat(transferQty),
                  note: transferNote,
                  destination: transferDest || defaultTransferDest(transferDestOptions, sourceWarehouseId) || 'production',
                });
                setTransferOpen(false);
                setTransferDest(defaultTransferDest(transferDestOptions, sourceWarehouseId));
                setTransferItem('');
                setTransferQty('');
                setTransferNote('');
                await loadData();
              } catch (e) {
                setTransferError(e.response?.data?.error || 'Помилка переміщення');
              } finally {
                setTransferLoading(false);
              }
            }}
          >
            {transferLoading ? <CircularProgress size={20} /> : 'Перемістити'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer request partial dialog */}
      <Dialog open={Boolean(transferReqDialog)} onClose={() => setTransferReqDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Перемістити на склад виробництва</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            <strong>{transferReqDialog?.name}</strong><br />
            Потрібно: {transferReqDialog?.quantity} | Переміщено: {(transferReqDialog?.transferred_quantity ?? 0).toFixed(3)} | Залишок: {Math.max(0, (transferReqDialog?.quantity ?? 0) - (transferReqDialog?.transferred_quantity ?? 0)).toFixed(3)}
          </Typography>
          <TextField
            label={`К-сть для переміщення, ${transferReqDialog?.unit || unitForCategory(transferReqDialog?.item_category)}`}
            type="number"
            fullWidth
            value={transferReqQty}
            onChange={(e) => setTransferReqQty(e.target.value)}
            inputProps={{
              min: (transferReqDialog?.unit || unitForCategory(transferReqDialog?.item_category)) === 'шт' ? 1 : 0.001,
              step: (transferReqDialog?.unit || unitForCategory(transferReqDialog?.item_category)) === 'шт' ? 1 : 0.001,
              max: Math.max(0, (transferReqDialog?.quantity ?? 0) - (transferReqDialog?.transferred_quantity ?? 0)),
            }}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferReqDialog(null)}>Скасувати</Button>
          <Button
            variant="contained"
            color="success"
            disabled={transferReqLoading || !transferReqQty || Number(transferReqQty) <= 0}
            onClick={handleTransferRequest}
          >
            {transferReqLoading ? <CircularProgress size={20} /> : 'Підтвердити'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default StockPage;

